// ============================================================================
// headerBottomBar — the phone bottom bar exactly as Header derives it
// (Design Constitution N02, Q09, Table C)
// ----------------------------------------------------------------------------
// One pure function so the bar can be tested with the real access, navigation
// and bar predicates (no mocks). It states no access rule of its own:
//   - a destination opens only when resolveSurfaceAccess ALLOWs it AND, for a
//     global destination (Admin, Processing, the programs), when the caller's
//     filtered destination list holds it — the same gate as the header menu and
//     program strip (Admin is admin-only even where the surface would allow it);
//   - a program bar needs real program access; a farm_team caller who reaches a
//     program's Dailys only through the daily-report exemption gets the outside
//     bar with Dailys lit.
// Route-level denial is unchanged: this only decides which tabs show.
// ============================================================================
import {
  canAccessDailyReports,
  canAccessProgram,
  canLightAccessView,
  DAILY_REPORT_VIEWS,
  isInactiveAccount,
  resolveSurfaceAccess,
  SURFACE_NAVIGATION,
  type AuthSession,
} from './access.js';
import {deriveBottomBar, type BottomBarModel, type BottomTab} from './bottomBar.js';
import {
  deriveNavSelection,
  filterGlobalDestinations,
  GLOBAL_DESTINATIONS,
  isProcessingRole,
  VIEW_TO_PROGRAM,
} from './globalNav.js';
import type {PageBottomBar} from './pageBottomBar.js';

const GLOBAL_DESTINATION_VIEWS = new Set(GLOBAL_DESTINATIONS.map((d) => d.view));

/** AuthContext's session: the access fields plus the signed-in user. */
export type HeaderAuth = (AuthSession & {user?: unknown}) | null;

export type HeaderBottomBarContext = {
  authState: HeaderAuth;
  view: string | null;
  showForm?: boolean;
  showBreedForm?: boolean;
  showFarrowForm?: boolean;
  /** The header menu or Bell panel, or any registered modal/drawer, is open. */
  overlayOpen?: boolean;
  pageBar?: PageBottomBar | null;
};

/** Whether the bar may offer `view` to this caller (both gates above). */
export function bottomBarCanOpen(authState: HeaderAuth): (view: string) => boolean {
  const isLight = authState?.role === 'light';
  const visible = new Set(
    filterGlobalDestinations({
      isLight,
      isAdmin: authState?.role === 'admin',
      role: authState?.role ?? null,
      canAccessProgram: (prog: string | null) => canAccessProgram(authState, prog),
      canLightAccessView,
    }).map((d) => d.view),
  );
  return (view: string) =>
    resolveSurfaceAccess({authState, view, viewToProgram: VIEW_TO_PROGRAM, isProcessingRole}).navigation ===
      SURFACE_NAVIGATION.ALLOW &&
    (!GLOBAL_DESTINATION_VIEWS.has(view) || visible.has(view));
}

export function deriveHeaderBottomBar(ctx: HeaderBottomBarContext): BottomBarModel {
  const {authState, view, pageBar} = ctx;
  const selection = deriveNavSelection({
    view,
    showForm: !!ctx.showForm,
    showBreedForm: !!ctx.showBreedForm,
    showFarrowForm: !!ctx.showFarrowForm,
    isLight: authState?.role === 'light',
    canAccessProgram: (prog: string | null) => canAccessProgram(authState, prog),
    dailyReportSectionViews: canAccessDailyReports(authState) ? [...DAILY_REPORT_VIEWS, 'cattlelog'] : null,
  });
  return deriveBottomBar({
    view,
    role: authState?.role ?? null,
    selection,
    programAccess: !!selection.program && canAccessProgram(authState, selection.program),
    canOpen: bottomBarCanOpen(authState),
    pageTabs: pageBar && !pageBar.hidden ? {tabs: pageBar.tabs as BottomTab[], activeKey: pageBar.activeKey} : null,
    signedIn: !!authState?.user,
    inactive: isInactiveAccount(authState),
    pastureField: !!pageBar && pageBar.hidden === true,
    overlayOpen: !!ctx.overlayOpen || !!ctx.showForm || !!ctx.showBreedForm || !!ctx.showFarrowForm,
  });
}
